import { Injectable } from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { IConfigService } from "app/config/iconfigservice";
import { AccountSettingsService, BaseService, FeatureManagerService } from "app/services";
import { AppService } from 'app/app.service';
import { BehaviorSubject, Observable, Subscription, catchError, filter, forkJoin, map, of, switchMap, take } from "rxjs";
import { FeatureToggle } from "app/enums";
import AccountSettings from "app/enums/account-settings.enum";
import { Account } from "app/models";
import { CaseSummarySettingsService } from "@Services/case/case-summary-settings.service";

export type UWWorkbenchHiddenNoteFeatureSetting = {
    featureToggle: boolean,
    accountSetting: boolean,
    editableNoteFeatureToggle?: boolean,
    editableNoteAccountSetting?: boolean,
    editableNoteDaysBackAccountSetting?: number,
    updatedRequirementsTableDesign?: boolean,
    duplicateTaskNotesInUWNotes?: boolean,
    updatedRequirementTaskNoteStyling?: boolean
}

@Injectable()
export default class UWNotesFeatureToggleDataService extends BaseService {

    // Holds undefined between an account reset and the new account's settings, so stale settings are never replayed.
    hiddenNotesFeatureSettingSub = new BehaviorSubject<UWWorkbenchHiddenNoteFeatureSetting | undefined>(undefined);
    hiddenNotesFeatureSetting$$ = this.hiddenNotesFeatureSettingSub.pipe(filter((setting): setting is UWWorkbenchHiddenNoteFeatureSetting => setting !== undefined));
    hiddenNotesFeatureSettingLoaded = false;
    forceReloadHiddenNotesFeatureSettings = false;
    private pendingLoad?: Subscription;
    // The application-scoped service caches settings for one account; a different account must not reuse them.
    private settingsAccountId?: string;

    isEditableNoteFeatureEnabled = false;
    isEditableNoteAccountSettingEnabled = false;
    editableNoteDaysBackAccountSetting: number;

    constructor(
        public configService: IConfigService,
        public httpClient: HttpClient,
        public appService: AppService,
        private featureManagerService: FeatureManagerService,
        private accountSettingService: AccountSettingsService,
        private caseSummarySettingsService: CaseSummarySettingsService
    ) {
        super(appService, configService, httpClient);
    }

    //#region Helpers

    reloadFeatureSettings(account: Account) {
        this.forceReloadHiddenNotesFeatureSettings = false;
        this.startLoad(this.getFeatureSettings(account, false));
        return this.hiddenNotesFeatureSetting$$;
    }

    private startLoad(settings$: Observable<UWWorkbenchHiddenNoteFeatureSetting>) {
        this.pendingLoad?.unsubscribe();
        this.pendingLoad = settings$.subscribe(settings => {
            this.hiddenNotesFeatureSettingLoaded = true;
            this.hiddenNotesFeatureSettingSub.next(settings);
        });
    }

    private getFeatureSettings(account: Account, useCachedAccountSettings: boolean): Observable<UWWorkbenchHiddenNoteFeatureSetting> {
        const getAccountSetting = (name: string): Observable<{ value?: string } | undefined> => useCachedAccountSettings
            ? this.caseSummarySettingsService.getAccountSetting(account, name).pipe(take(1))
            : this.accountSettingService.getAccountSetting(account, name);
        return forkJoin([
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchHiddenNotesAccountSetting),
            getAccountSetting(AccountSettings.UWWorkbenchHiddenNotes),
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchEditableNotesAccountSetting),
            getAccountSetting(AccountSettings.UWWorkbenchEditableNotes),
            getAccountSetting(AccountSettings.UWWorkbenchEditableNotesDuration),            
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchRequirementsTableDesign, account.clientId),          
            this.featureManagerService.isEnabled(FeatureToggle.UWWorkbenchRequirementsTaskAndRequirementStyling, account.clientId),
            getAccountSetting(AccountSettings.UWWorkbenchDuplicateTaskNotesInUWNotes),
        ]).pipe(map(([
            hiddenNoteFeatureToggle, 
            hiddenNoteAccountSetting,
            editableNoteFeatureToggle,
            editableNoteAccountSetting,
            editableNoteDaysBackAccountSetting,
            updatedRequirementsTableDesign,
            updatedRequirementTaskNoteStyling,
            duplicateTaskNotesInUWNotes
        ]) => {
            const featureToggleSetting = hiddenNoteFeatureToggle ?? false;
            const accountSetting = (hiddenNoteAccountSetting) ? (hiddenNoteAccountSetting?.value?.toLocaleLowerCase() === 'true') : false;
            this.isEditableNoteFeatureEnabled = editableNoteFeatureToggle ?? false;
            this.isEditableNoteAccountSettingEnabled = (editableNoteAccountSetting) ? (editableNoteAccountSetting?.value?.toLocaleLowerCase() === 'true') : false;
            this.editableNoteDaysBackAccountSetting = +editableNoteDaysBackAccountSetting?.value || -1;
            return {
                featureToggle: featureToggleSetting,
                accountSetting: accountSetting,
                editableNoteFeatureToggle: this.isEditableNoteFeatureEnabled,
                editableNoteAccountSetting: this.isEditableNoteAccountSettingEnabled,
                editableNoteDaysBackAccountSetting: this.editableNoteDaysBackAccountSetting,
                updatedRequirementsTableDesign: updatedRequirementsTableDesign,
                updatedRequirementTaskNoteStyling: updatedRequirementTaskNoteStyling,
                duplicateTaskNotesInUWNotes:(duplicateTaskNotesInUWNotes) ? (duplicateTaskNotesInUWNotes?.value?.toLocaleLowerCase() === 'true') : false
            };
        }));
    }

    private isDeduplicationEnabled(account: Account): Observable<boolean> {
        if (!account?.clientId) return of(false);

        return this.featureManagerService.isEnabled(FeatureToggle.ConfigWorkbenchDeduplicateApiCalls, account.clientId).pipe(
            take(1),
            catchError(error => {
                this.appService.showResponseErrorMsg(error);
                return of(false);
            })
        );
    }

    //#endregion

    //#region LifeCycle

    ngOnDestroy() {
        this.pendingLoad?.unsubscribe();
    }

    //#endregion
    loadHiddenNotesFeatureSetting(account: Account) {
        if (account?.id !== this.settingsAccountId) {
            this.resetForAccountChange();
            this.settingsAccountId = account?.id;
        }

        if (!this.forceReloadHiddenNotesFeatureSettings && this.hiddenNotesFeatureSettingLoaded)
            return this.hiddenNotesFeatureSetting$$;

        if (this.forceReloadHiddenNotesFeatureSettings)
            return this.reloadFeatureSettings(account);

        this.startLoad(this.isDeduplicationEnabled(account).pipe(
            switchMap(useCachedAccountSettings => this.getFeatureSettings(account, useCachedAccountSettings))
        ));

        return this.hiddenNotesFeatureSetting$$;
    }

    forceOnNextLoadHiddenNotesFeatureSetting() {
        this.forceReloadHiddenNotesFeatureSettings = true;
    }

    /** Clears account-scoped state so the next load reads the new account without forcing the uncached path. */
    resetForAccountChange() {
        this.hiddenNotesFeatureSettingLoaded = false;
        this.pendingLoad?.unsubscribe();
        this.pendingLoad = undefined;
        this.settingsAccountId = undefined;
        this.hiddenNotesFeatureSettingSub.next(undefined);
    }
}